import React from 'react';
import planIcon from '../../../../../../assets/image/subscription/plan-icon.svg';
import Button from '../../../../../../component/ui/Button';
import { format2DigitDate, formatLastServedDate } from '../../../../../../utils/constant';
import { axiosInstanceV2 } from '../../../../../../services/axios';
import { toast } from 'react-toastify';
import useSubscription from '../../../../../../hooks/useSubscription';
import {
	cancelSubscription,
	undoScheduledCancellation,
} from '../../../../../../services/subscription';

export default function CurrentPlanComponents({
	currentPlan,
	setIsOpenCancelSubscriptionModal,
	setSubscriptionScreenType,
}) {
	const {
		isSubscriptionFree,
		isSubscriptionActive,
		isSubscriptionRecurring,
		isPendingPayment,
		isSubscriptionComplimentary,
		complimentaryUntil,
	} = useSubscription();

	const handleCancelSubscription = async (cancel_at_cycle_end = 1) => {
		try {
			await cancelSubscription({ cancel_at_cycle_end });
			toast.success('Switched to Free plan');
			window.location.reload();
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Unable to switch to free plan');
		}
	};

	// The badge is driven by the tone the backend sends, not by re-listing every
	// status string here. The previous switch matched raw enum names
	// ('Created', 'Authenticated', 'Halted', 'Superseded') that the API never
	// returns, while the strings it DOES return — 'Cancels at end date',
	// 'Payment Pending', 'Payment Halted', 'Inactive' — had no case and fell to
	// the grey default. A failed payment therefore looked as unremarkable as an
	// inactive plan.
	const TONE_STYLES = {
		success: { backgroundColor: '#F0FFE6', color: '#68C230' },
		info: { backgroundColor: '#EEF6FF', color: '#0D6EFD' },
		warning: { backgroundColor: '#FFF4E5', color: '#E67E22' },
		danger: { backgroundColor: '#FDECEC', color: '#DC3545' },
		neutral: { backgroundColor: '#F5F5F5', color: '#6C757D' },
	};

	// Fallback for any payload sent before status_display existed.
	const LEGACY_STATUS_TONES = {
		Active: 'success',
		'Free Plan': 'success',
		'Payment Pending': 'info',
		'Cancels at end date': 'warning',
		Cancelling: 'warning',
		Paused: 'warning',
		'Payment Halted': 'danger',
		'Payment Failed': 'danger',
		Cancelled: 'neutral',
		Expired: 'neutral',
		Inactive: 'neutral',
	};

	const statusDisplay = currentPlan?.status_display;
	// 'No Subscription' rather than 'Unknown'. This fallback is only reached
	// when the API returned no subscription at all — a real, describable state
	// (and the exact wording getSubscriptionStatusPresentation uses for it).
	// 'Unknown' read like a failure and sent people hunting for a bug when the
	// organisation simply had no plan.
	const statusLabel = statusDisplay?.label ?? currentPlan?.status ?? 'No Subscription';
	const statusTone = statusDisplay?.tone ?? LEGACY_STATUS_TONES[currentPlan?.status] ?? 'neutral';
	const statusStyle = TONE_STYLES[statusTone] ?? TONE_STYLES.neutral;

	// The plan-name box already renders "Free" for a free-plan org (see
	// `currentPlan?.plan?.name` below), and the backend's own status label for
	// that state is literally the string "Free Plan" — so the two sat right
	// next to each other reading "Free" "Free Plan", the same redundant pair
	// repeated twice for no added information. Every paid plan already shows
	// this cleanly ("Professional" next to "Active"): mirror that here rather
	// than inventing new copy — the tone/colour still comes from the backend.
	const badgeLabel = isSubscriptionFree ? 'Active' : statusLabel;

	// Undoes a pending "cancel at cycle end" request. Nothing was ever told
	// to Razorpay for it, so this just removes the scheduled job — the
	// subscription keeps renewing normally, no payment/checkout involved.
	const handleResumeSubscription = async () => {
		try {
			const result = await undoScheduledCancellation();
			toast.success(result?.data?.message ?? 'Subscription will continue renewing normally.');
			window.location.reload();
		} catch (error) {
			toast.error(error?.response?.data?.message ?? 'Failed to resume subscription');
		}
	};

	const handleCancelScheduledDowngrade = async () => {
		try {
			const token = localStorage.getItem('authToken');
			await axiosInstanceV2.post(
				'/subscription/cancel-scheduled-downgrade',
				{},
				{
					headers: {
						Authorization: `Bearer ${token}`,
					},
				},
			);
			toast.success('Scheduled downgrade cancelled.');
			window.location.reload();
		} catch (error) {
			console.error('Failed to cancel scheduled downgrade:', error);
			toast.error(error?.response?.data?.message ?? 'Unable to cancel scheduled downgrade.');
		}
	};

	return (
		<div>
			<div className="fs-3 fw-semibold py-2">Current Subscription</div>
			{isPendingPayment && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#EEF6FF', border: '1px solid #6EA8FE' }}
				>
					<div className="d-flex align-items-center gap-2">
						<span style={{ fontSize: 20 }}>⏳</span>
						<div className="fw-semibold text-dark">Payment Pending</div>
					</div>
					<div className="mt-2 fs-5">
						Your subscription to <strong>{currentPlan?.plan?.name}</strong> was created but payment
						hasn't been completed yet.
					</div>
				</div>
			)}

			{/*
			  Different from the "Payment Pending" block above: that fires when
			  `currentPlan` ITSELF is unpaid (a brand-new subscriber). This fires
			  when an existing, fully active plan has a SEPARATE upgrade/downgrade
			  replacement waiting on a new mandate — the case where nothing else on
			  this page hints that anything changed, because `currentPlan` here is
			  still the old plan.
			*/}
			{currentPlan?.pending_change && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#EEF6FF', border: '1px solid #6EA8FE' }}
				>
					<div className="d-flex align-items-center gap-2">
						<span style={{ fontSize: 20 }}>⏳</span>
						<div className="fw-semibold text-dark">Plan change processing</div>
					</div>
					<div className="mt-2 fs-5">
						Your move to <strong>{currentPlan.pending_change.plan_name ?? 'a new plan'}</strong> is
						waiting on payment confirmation. This can take a few minutes — refresh to check for
						updates.
					</div>
				</div>
			)}

			{/*
			  The scheduled-downgrade / plan-change notice deliberately does NOT
			  live here. This component is only ever rendered by the subscription
			  settings page, immediately below that page's own banner for the same
			  state — so having one here too printed the identical message twice,
			  one above the other, with different date formats. The page-level
			  banner is the one to keep: it carries the "Cancel downgrade" button,
			  so it is the actionable copy rather than a second read-only echo.
			*/}

			{currentPlan?.is_scheduled_for_cancellation && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#FFF4E5', border: '1px solid #F5B97F' }}
				>
					<div className="d-flex align-items-center gap-2">
						<span style={{ fontSize: 20 }}>⚠️</span>
						<div className="fw-semibold text-dark">Cancellation Scheduled</div>
					</div>
					<div className="mt-2 fs-5">
						Your subscription is scheduled to end on{' '}
						<strong>{format2DigitDate(currentPlan?.current_end)}</strong> and will switch to the
						Free plan. You'll keep full access until then — no charge will be made.
					</div>
					<div className="mt-1 fs-5 text-muted">
						Changed your mind? Resume your subscription any time before then to keep it running with
						no interruption.
					</div>
				</div>
			)}

			{/* The complimentary explanation lives here, above the card, rather than
			    under the plan name: it is a full sentence, and inline it squeezed the
			    Amount / Validity / Status columns. */}
			{isSubscriptionComplimentary && statusDisplay?.description && (
				<div
					className="mb-3 rounded-3 p-3"
					style={{ background: '#F0FFE6', border: '1px solid #B7E4A0' }}
				>
					<div className="d-flex align-items-center gap-2">
						<span style={{ fontSize: 20 }}>🎁</span>
						<div className="fw-semibold text-dark">Complimentary plan</div>
					</div>
					<div className="mt-2 fs-5">{statusDisplay.description}</div>
				</div>
			)}

			{isSubscriptionActive &&
				!isSubscriptionRecurring &&
				!isSubscriptionFree &&
				!isSubscriptionComplimentary &&
				!currentPlan?.is_downgrade_scheduled &&
				!currentPlan?.is_scheduled_for_cancellation && (
					<div
						className="mb-3 rounded-3 p-3"
						style={{ background: '#F5F5F5', border: '1px solid #D9D9D9' }}
					>
						<div className="fs-5 text-muted">
							This is a one-time plan valid until{' '}
							<strong>{format2DigitDate(currentPlan?.current_end)}</strong>. It won't auto-renew —
							once it ends, your account moves to the Free plan automatically. You can subscribe
							again anytime.
						</div>
					</div>
				)}

			<div
				className="d-flex flex-wrap flex-xl-nowrap justify-content-between align-items-center rounded-3 p-2"
				style={{
					border: '1px solid rgba(85, 91, 103, 0.15)',
					backgroundColor: 'rgba(252, 252, 252, 1)',
				}}
			>
				<div className="d-flex justify-content-start align-items-center p-2">
					<img
						src={planIcon}
						className="px-2"
						alt="icon"
					/>
					<div className="px-3">
						<div className="d-flex gap-3 align-items-center flex-wrap">
							<div className="fs-4 font-medium">{`${currentPlan?.plan?.name || 'Free Plan'}`}</div>
							{/* width was pinned to 50px, which clipped anything longer than
							    "Active" — "Payment Pending" and "Cancelling" were unreadable.
							    Size to the content instead. */}
							<span
								className="px-3 py-1 fs-5 font-small rounded-4 text-nowrap"
								style={{ ...statusStyle, display: 'inline-block' }}
								title={statusDisplay?.description ?? ''}
							>
								{badgeLabel}
							</span>
						</div>
						{statusDisplay?.description && !isSubscriptionComplimentary && (
							<div className="fs-5 text-muted mt-1">{statusDisplay.description}</div>
						)}
						<div className="fs-5 font-small">
							Description: {currentPlan?.plan?.description || 'N/A'}
						</div>
						<div className="fs-5 font-small">
							{isSubscriptionFree
								? 'No payment subscription'
								: isSubscriptionComplimentary
									? 'No payment subscription'
									: `Subscription Id : ${currentPlan?.razorpay_subscription_id}`}
						</div>
					</div>
				</div>
				<div className="subscription-divider"></div>
				<div className="px-3 py-2">
					<div className="d-flex gap-4 align-items-center">
						<div className="fs-4 font-medium">{'Amount'}</div>
					</div>
					{isSubscriptionFree ? (
						<>
							<div className="fs-5 font-small text-success">Free Forever</div>
							<div className="fs-5 font-small">No recurring charges</div>
						</>
					) : isSubscriptionComplimentary ? (
						<>
							<div className="fs-5 font-small text-success">Complimentary</div>
							<div className="fs-5 font-small">No charges</div>
						</>
					) : (
						<>
							<div className="fs-5 font-small">
								₹ {currentPlan?.amount} / {currentPlan?.billing_period}
							</div>
							<div className="fs-5 font-small">{`Billed ${currentPlan?.billing_period}`}</div>
						</>
					)}
				</div>
				<div className="subscription-divider"></div>
				<div className="px-3 py-2">
					<div className="d-flex gap-4 align-items-center">
						<div className="fs-4 font-medium">{'Validity'}</div>
					</div>
					{isSubscriptionFree ? (
						// Free never has a current_end to show — the plan doesn't expire,
						// so a "start - end" range with a blank/dangling end date read as
						// broken. State the actual fact instead: it just doesn't end.
						<div className="fs-5 font-small text-success">No expiry</div>
					) : isSubscriptionComplimentary ? (
						// A complimentary plan may have no end date at all — the generic
						// "start - end" range below would print a dangling dash for it.
						// Not formatLastServedDate either: a complimentary end date is the
						// END of that day (23:59 IST), so it is itself the last day served.
						<div className="fs-5 font-small">
							{complimentaryUntil
								? `${currentPlan?.current_start ? format2DigitDate(currentPlan.current_start) : ''} - ${format2DigitDate(complimentaryUntil)}`
								: 'No end date'}
						</div>
					) : (
						// The range end is the last day of SERVICE, not `current_end`.
						// Access stops at current_end (the backend checks
						// `current_end > now` strictly), so printing it here claimed a
						// day the customer never gets — see formatLastServedDate.
						// The lines below deliberately still use current_end itself:
						// they describe the transition, which does happen on that day.
						<div className="fs-5 font-small">{`${currentPlan?.current_start ? format2DigitDate(currentPlan.current_start) : ''} - ${currentPlan?.current_end ? formatLastServedDate(currentPlan.current_end) : ''}`}</div>
					)}
					{isSubscriptionFree ? null : isSubscriptionComplimentary ? (
						complimentaryUntil ? (
							<div className="fs-5 font-small text-muted">Moves to Free after this date</div>
						) : null
					) : currentPlan?.is_downgrade_scheduled ? (
						<div className="fs-5 font-small text-primary">
							Downgrades on {format2DigitDate(currentPlan.downgrade_at)}
						</div>
					) : currentPlan?.next_billing_date ? (
						<div className="fs-5 font-small">
							Next billing on {format2DigitDate(currentPlan.next_billing_date)}
						</div>
					) : currentPlan?.is_scheduled_for_cancellation ? (
						<div className="fs-5 font-small text-warning">
							Subscription ends on {format2DigitDate(currentPlan.current_end)}
						</div>
					) : !isSubscriptionRecurring ? (
						<div className="fs-5 font-small text-muted">
							Moves to Free on {format2DigitDate(currentPlan?.current_end)}
						</div>
					) : null}
				</div>
				<div className="subscription-divider"></div>
				<div className="px-3 py-2">
					<div className="d-flex gap-4 align-items-center">
						<div className="fs-4 font-medium">{'Status'}</div>
					</div>
					<div
						className="p-2 fs-5 font-small rounded-4"
						style={{
							backgroundColor: currentPlan?.is_downgrade_scheduled
								? '#EEF6FF'
								: currentPlan?.is_scheduled_for_cancellation
									? '#FFF4E5'
									: '#F0FFE6',
							color: currentPlan?.is_downgrade_scheduled
								? '#0D6EFD'
								: currentPlan?.is_scheduled_for_cancellation
									? '#E67E22'
									: '#68C230',
						}}
					>
						{currentPlan?.is_downgrade_scheduled
							? 'Downgrade Scheduled'
							: currentPlan?.is_scheduled_for_cancellation
								? 'Scheduled for Cancellation'
								: statusLabel}
					</div>
					<div className="fs-5 font-small">
						{!isSubscriptionFree && isSubscriptionRecurring && (
							<div className="fs-5 font-small">
								Auto Renewal
								<span
									className={`ms-2 fw-semibold ${
										currentPlan?.auto_renewal ? 'text-success' : 'text-danger'
									}`}
								>
									{currentPlan?.auto_renewal ? 'ON' : 'OFF'}
								</span>
							</div>
						)}
					</div>
				</div>
				<div className="align-items-center justify-content-end px-3 py-2 ">
					{/* Nothing to manage for a complimentary plan — no payment method,
					    invoices or Razorpay subscription behind it. */}
					{!isSubscriptionFree && !isSubscriptionComplimentary && (
						<Button
							type="button"
							className="mt-0 btn-primary fs-5 fw-light add-pro py-1 px-4"
							button="Manage Billing"
							onClick={() => setSubscriptionScreenType('manage-billing')}
						/>
					)}
					{currentPlan?.is_downgrade_scheduled && (
						<div>
							<div
								className="mt-3 fs-5 font-small text-warning cursor-pointer"
								onClick={handleCancelScheduledDowngrade}
							>
								Cancel Downgrade
							</div>
						</div>
					)}
					{/* Cancel/Resume applies to every paid plan, not just recurring ones —
					    a non-recurring (one-time) plan can be cancelled too; it just has
					    no Razorpay mandate to stop, so "cancelling" only acknowledges the
					    request and confirms the existing move-to-Free-at-period-end
					    behaviour (see cancelSubscription in the backend). */}
					{/* A complimentary plan has no payment to cancel; it moves to Free by
					    itself when it ends (the backend refuses the cancel too). */}
					{!isSubscriptionFree &&
						!isSubscriptionComplimentary &&
						isSubscriptionActive &&
						!currentPlan?.is_downgrade_scheduled &&
						(currentPlan?.is_scheduled_for_cancellation ? (
							<div
								className="mt-3 fs-5 font-small text-primary cursor-pointer"
								onClick={handleResumeSubscription}
							>
								Resume Subscription
							</div>
						) : (
							<div
								className="mt-3 fs-5 font-small text-danger cursor-pointer"
								onClick={() => setIsOpenCancelSubscriptionModal(true)}
							>
								Cancel Subscription
							</div>
						))}
					{isPendingPayment && (
						<div className="mt-3 d-flex gap-3">
							<div
								className="fs-5 font-small text-danger cursor-pointer align-self-center"
								onClick={() => handleCancelSubscription(0)}
							>
								Switch to Previous Plan instead
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
